import React, { useEffect, useState } from 'react'
import { ClipboardList, Save, CheckCircle2, Loader2, Upload, Plus, Trash2, FileText, Download } from 'lucide-react'
import { assignmentApi, type RubricItem } from '../api/lessonBuilderApi'
import { genId } from '../lib/id'
import AssignmentInline from '../components/AssignmentInline'
import PdfPreview from '../components/PdfPreview'
import type { AssignmentCard } from '../types/LessonDoc'
import type { CardDef, CardEditorProps, CardPreviewProps } from './types'

const yLetter = (i: number) => String.fromCharCode(97 + i) // a, b, c…

// Rubric phẳng [{label,max,cau}] → cấu trúc Câu→ý để sửa lại
const rubricToCaus = (rubric: RubricItem[]): { items: { y: string; max: number }[] }[] => {
  const groups: { cau: string; items: { y: string; max: number }[] }[] = []
  rubric.forEach((r) => {
    const cau = r.cau || ''
    const y = (r.label || '').replace(/^[a-zA-Z]\)\s*/, '')
    const last = groups[groups.length - 1]
    if (last && last.cau === cau) last.items.push({ y, max: r.max })
    else groups.push({ cau, items: [{ y, max: r.max }] })
  })
  return groups.map((g) => ({ items: g.items }))
}

const Editor: React.FC<CardEditorProps> = ({ card, onChange }) => {
  const c = card as AssignmentCard
  const [saving, setSaving] = useState(false)
  const [msg, setMsg] = useState('')
  const [file, setFile] = useState<File | null>(null)
  const [dragOver, setDragOver] = useState(false)
  const [curAttach, setCurAttach] = useState<{ url: string; type: string } | null>(null)
  const [caus, setCaus] = useState<{ items: { y: string; max: number }[] }[]>([])

  // Nạp rubric + đề đã đính kèm (nếu bài tập đã lưu) để sửa tiếp
  useEffect(() => {
    if (!c.assignmentId) return
    assignmentApi.get(c.assignmentId).then((r) => {
      if (!r) return
      const rub = r.assignment.rubric || []
      if (rub.length) setCaus(rubricToCaus(rub))
      if (r.assignment.attachment_url) setCurAttach({ url: r.assignment.attachment_url, type: r.assignment.attachment_type || '' })
    }).catch(() => {})
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [c.assignmentId])

  const hasRubric = caus.some((x) => x.items.length > 0)
  const rubricTotal = Math.round(caus.reduce((a, x) => a + x.items.reduce((s, it) => s + (it.max || 0), 0), 0) * 100) / 100
  const addCau = () => setCaus((p) => [...p, { items: [{ y: '', max: 1 }] }])
  const delCau = (ci: number) => setCaus((p) => p.filter((_, k) => k !== ci))
  const addY = (ci: number) => setCaus((p) => p.map((x, k) => (k === ci ? { items: [...x.items, { y: '', max: 1 }] } : x)))
  const delY = (ci: number, yi: number) => setCaus((p) => p.map((x, k) => (k === ci ? { items: x.items.filter((_, j) => j !== yi) } : x)))
  const setY = (ci: number, yi: number, patch: Partial<{ y: string; max: number }>) =>
    setCaus((p) => p.map((x, k) => (k === ci ? { items: x.items.map((it, j) => (j === yi ? { ...it, ...patch } : it)) } : x)))

  // Nhận file đề (click / kéo-thả): ảnh / PDF / Word
  const pickFile = (f?: File | null) => {
    if (!f) return
    const ok = f.type.startsWith('image/') || f.type === 'application/pdf' || /\.(png|jpe?g|webp|gif|pdf|docx?)$/i.test(f.name)
    if (!ok) { setMsg('Chỉ nhận ảnh, PDF hoặc Word (.doc/.docx).'); return }
    setFile(f)
  }

  const save = async () => {
    if (!c.title?.trim()) { setMsg('Hãy nhập tên bài tập.'); return }
    // Gom Câu → ý thành rubric phẳng (kèm cau); tổng tự cộng.
    const flatRubric: RubricItem[] = []
    caus.forEach((cu, ci) => {
      const cau = `Câu ${ci + 1}`
      cu.items.forEach((it, yi) => flatRubric.push({ cau, label: `${yLetter(yi)})${it.y.trim() ? ' ' + it.y.trim() : ''}`, max: Math.max(0, it.max || 0) }))
    })
    setSaving(true); setMsg('')
    try {
      const a = await assignmentApi.save({
        id: c.assignmentId || undefined,
        title: c.title.trim(),
        prompt: c.prompt || '',
        max_score: c.maxScore || 10,
        allow_file: c.allowFile !== false,
        rubric: flatRubric.length ? flatRubric : undefined,
      })
      if (!c.assignmentId) onChange({ assignmentId: a.id } as Partial<AssignmentCard> as any)
      let extra = ''
      if (file) {
        try {
          const ua = await assignmentApi.uploadAttachment(a.id, file)
          setCurAttach({ url: ua.attachment_url || '', type: ua.attachment_type || '' })
          setFile(null); extra = ' Đã đính kèm đề.'
        } catch (e) {
          let why = ''
          try { const m = String((e as Error).message || ''); const j = m.slice(m.indexOf('{')); const d = JSON.parse(j); if (d?.detail) why = ' (' + d.detail + ')' } catch { /* giữ chung */ }
          extra = ' — nhưng đính kèm đề chưa được' + why
        }
      }
      setMsg('✅ Đã lưu bài tập. Học viên có thể nộp ngay trong bài giảng.' + extra)
    } catch {
      setMsg('Lưu chưa thành công, vui lòng thử lại.')
    } finally { setSaving(false) }
  }

  return (
    <div className="space-y-2.5">
      <div className="p-3 rounded-lg bg-blue-50 border border-blue-100 text-blue-700 text-sm flex items-center gap-2">
        <ClipboardList size={16} /> <span><strong>Bài tập tự luận</strong> — học viên nộp bài ngay trong bài giảng; giáo viên chấm tay ở trang "Bài tập" (chấm theo Câu/ý + bút ghi điểm). Điểm vào Sổ điểm.</span>
      </div>
      <div>
        <label className="text-xs font-bold text-gray-500 uppercase">Tên bài tập</label>
        <input className="w-full p-2 border border-gray-200 rounded-lg text-sm outline-none focus:ring-2 focus:ring-blue-200"
          value={c.title || ''} onChange={(e) => onChange({ title: e.target.value } as any)} placeholder="VD: Viết đoạn văn phân tích…" />
      </div>
      <div>
        <label className="text-xs font-bold text-gray-500 uppercase">Đề bài / Yêu cầu (gõ tay, có thể dùng $LaTeX$)</label>
        <textarea className="w-full min-h-[80px] p-2 border border-gray-200 rounded-lg text-sm outline-none focus:ring-2 focus:ring-blue-200 resize-y"
          value={c.prompt || ''} onChange={(e) => onChange({ prompt: e.target.value } as any)} placeholder="Mô tả yêu cầu… hoặc tải đề ảnh/PDF/Word bên dưới" />
      </div>

      {/* Tải ĐỀ: ảnh/PDF giữ nguyên; Word → in sang PDF giữ format */}
      <div>
        <label className="text-xs font-bold text-gray-500 uppercase">Tải đề (ảnh / PDF / Word có công thức)</label>
        <label
          onDragOver={(e) => { e.preventDefault(); setDragOver(true) }}
          onDragLeave={() => setDragOver(false)}
          onDrop={(e) => { e.preventDefault(); setDragOver(false); pickFile(e.dataTransfer.files?.[0]) }}
          className={`mt-1 flex flex-col items-center justify-center gap-1 px-3 py-4 border-2 border-dashed rounded-lg text-sm cursor-pointer transition-colors ${dragOver ? 'border-blue-400 bg-blue-50 text-blue-700' : 'border-gray-300 text-gray-600 hover:border-blue-300 hover:bg-gray-50'}`}
        >
          <Upload size={20} className={dragOver ? 'text-blue-500' : 'text-gray-400'} />
          {file ? <span className="font-semibold text-blue-700">{file.name}</span>
            : <span><b className="text-blue-600">Kéo thả</b> file Word/ảnh/PDF vào đây, hoặc <b className="text-blue-600">bấm để chọn</b></span>}
          <input type="file" accept="image/*,application/pdf,.doc,.docx,application/vnd.openxmlformats-officedocument.wordprocessingml.document" className="hidden"
            onChange={(e) => pickFile(e.target.files?.[0])} />
        </label>
        <p className="text-[11px] text-gray-400 mt-1">Ảnh/PDF giữ nguyên cho HS xem &amp; tải. Word: in sang PDF giữ nguyên format. Đề sẽ đính kèm khi bấm "Lưu bài tập".</p>
        {curAttach?.url && (
          <div className="mt-2 rounded-lg border border-emerald-200 bg-emerald-50/40 p-2">
            <div className="flex items-center justify-between mb-1.5">
              <span className="text-xs font-bold text-emerald-700 inline-flex items-center gap-1"><FileText size={13} /> Xem trước đề ({curAttach.type === 'pdf' ? 'PDF' : 'ảnh'})</span>
              <a href={curAttach.url} download target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-xs font-bold text-slate-600 hover:text-slate-800"><Download size={13} /> Tải xuống</a>
            </div>
            {curAttach.type === 'pdf'
              ? <PdfPreview url={curAttach.url} className="max-h-96 overflow-auto rounded border border-gray-200 bg-white p-1" />
              : <img src={curAttach.url} alt="Xem trước đề" className="w-full rounded border border-gray-200 bg-white" />}
          </div>
        )}
      </div>

      <div className="flex items-center gap-4 flex-wrap">
        <div>
          <label className="text-xs font-bold text-gray-500 uppercase">Thang điểm {hasRubric && '(theo Câu/ý)'}</label>
          <input type="number" min={1} disabled={hasRubric} className="w-24 p-2 border border-gray-200 rounded-lg text-sm outline-none focus:ring-2 focus:ring-blue-200 disabled:bg-gray-100"
            value={hasRubric ? rubricTotal : (c.maxScore ?? 10)} onChange={(e) => onChange({ maxScore: Number(e.target.value) || 10 } as any)} />
        </div>
        <label className="flex items-center gap-1.5 text-sm text-gray-600 mt-4">
          <input type="checkbox" checked={c.allowFile !== false} onChange={(e) => onChange({ allowFile: e.target.checked } as any)} /> Cho nộp kèm file lời giải
        </label>
      </div>

      {/* Chấm điểm theo CÂU → Ý (a,b,c…) — tổng tự cộng */}
      <div>
        <label className="text-xs font-bold text-gray-500 uppercase">Chấm điểm theo Câu → ý (tuỳ chọn)</label>
        <p className="text-[11px] text-gray-400 mb-1.5">Bấm <b>“+ Câu”</b> tạo Câu 1, 2, 3…; trong mỗi câu bấm <b>“+ ý”</b> để thêm ý a, b, c… và nhập điểm. Tổng tự cộng.</p>
        <div className="space-y-2">
          {caus.map((cu, ci) => (
            <div key={ci} className="rounded-xl border border-gray-200 p-2.5 bg-gray-50/60">
              <div className="flex items-center justify-between mb-1.5">
                <span className="text-sm font-black text-slate-700">Câu {ci + 1}</span>
                <button onClick={() => delCau(ci)} className="text-rose-400 hover:text-rose-600 inline-flex items-center gap-1 text-xs font-semibold"><Trash2 size={13} /> Xoá câu</button>
              </div>
              <div className="space-y-1.5">
                {cu.items.map((it, yi) => (
                  <div key={yi} className="flex items-center gap-2">
                    <span className="w-6 text-center font-bold text-slate-500">{yLetter(yi)})</span>
                    <input value={it.y} onChange={(e) => setY(ci, yi, { y: e.target.value })}
                      placeholder="Nội dung ý (tuỳ chọn)" className="flex-1 p-1.5 border border-gray-200 rounded text-sm outline-none focus:ring-2 focus:ring-blue-200" />
                    <input type="number" min={0} step="0.25" value={it.max} onChange={(e) => setY(ci, yi, { max: Math.max(0, Number(e.target.value) || 0) })}
                      className="w-16 p-1.5 border border-gray-200 rounded text-sm text-center outline-none focus:ring-2 focus:ring-blue-200" />
                    <span className="text-[11px] text-gray-400">đ</span>
                    <button onClick={() => delY(ci, yi)} className="p-1 text-rose-300 hover:text-rose-600"><Trash2 size={13} /></button>
                  </div>
                ))}
              </div>
              <button onClick={() => addY(ci)} className="mt-1.5 text-xs text-blue-600 font-semibold inline-flex items-center gap-1"><Plus size={12} /> Thêm ý</button>
            </div>
          ))}
        </div>
        <div className="flex items-center justify-between mt-2">
          <button onClick={addCau} className="text-sm text-blue-600 font-bold inline-flex items-center gap-1"><Plus size={14} /> Thêm Câu</button>
          {hasRubric && <span className="text-xs font-bold text-emerald-700">Tổng thang: {rubricTotal}đ (tự áp)</span>}
        </div>
      </div>

      <div className="flex items-center gap-2">
        <button onClick={save} disabled={saving}
          className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-blue-600 text-white text-sm font-bold hover:bg-blue-700 disabled:opacity-50">
          {saving ? <Loader2 size={15} className="animate-spin" /> : (c.assignmentId ? <CheckCircle2 size={15} /> : <Save size={15} />)}
          {c.assignmentId ? 'Cập nhật bài tập' : 'Lưu bài tập'}
        </button>
        {c.assignmentId && <span className="text-[11px] font-bold text-emerald-600">Đã lưu · học viên nộp được</span>}
      </div>
      {msg && <p className="text-xs text-gray-600">{msg}</p>}
      <p className="text-[11px] text-gray-400">Phải bấm "Lưu bài tập" thì học viên mới nộp được. Nhớ Lưu bài giảng sau đó.</p>
    </div>
  )
}

const Preview: React.FC<CardPreviewProps> = ({ card }) => {
  const c = card as AssignmentCard
  return <AssignmentInline assignmentId={c.assignmentId} />
}

export const assignmentCardDef: CardDef = {
  type: 'assignment',
  label: 'Bài tập tự luận',
  description: 'Học viên nộp bài, giáo viên chấm tay (điểm vào Sổ điểm)',
  icon: ClipboardList,
  color: 'bg-blue-50 text-blue-600',
  createDefault: (): AssignmentCard => ({ id: genId(), type: 'assignment', title: 'Bài tập tự luận', prompt: '', maxScore: 10, allowFile: true }),
  Editor,
  Preview,
}
